import { NoticeOperationResult } from '@core/databus/IDataSyncManager'
import type { ActionNoticeDataBus } from '@/core/databus/DataBus'
import { buildActionDataBus } from '@/core/databus/DataBusFactory'
import type { ActionNoticeData } from '@/core/databus/DataSyncManager'
import { Action, NoticeCategory, NoticeType as NoticeActionType } from '@/core/databus/IMessage'
import { orderBy, debounce } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { SystemAppId } from '@core/application/ApplicationConstants'
import { NoticeStore } from './NoticeStore'
import { TimeConstants } from '@core/Constants'

export type NoticeDataType = ActionNotice | TransmissionNotice
type NoticeType = ActionNoticeData | TransmissionData
interface ReadInfo {
  isRead: boolean
}

export interface TransmissionData extends SysTransmissionData {
  appId: string
  category: NoticeCategory.TRANSMISSION
  timestamp: number
  isEmphasized: boolean
}

export interface SysTransmissionData {
  id: string
  upload: TransmissionMessage
  download: TransmissionMessage
}

export interface SysNotice {
  /**
   *  各自微应用或者基座代表的appId
   */
  appId?: string
  msg: string
  /**
   * 通知类型 是否带有按钮
   * 默认值:{@link NoticeActionType.INFO}
   */
  type?: NoticeActionType
  /**
   * 通知动作的类型
   * 默认值: {@link Action.NAV_TO}
   */
  action?: Action
  /**
   * 可选项 是否是强类型的通知，默认是false
   */
  isEmphasized?: boolean
  actionBtnLabel?: string
  extraQuery?: string
}

export interface ActionNotice extends ActionNoticeData, ReadInfo { }

export interface TransmissionNotice extends TransmissionData, ReadInfo { }

export interface TransmissionMessage {
  successCount: number //成功数
  totalCount: number //总数
  errorCount: number //失败数
  residualCount: number //剩余数
  pausedCount: number //暂停数
}

/**
 * @internal
 * for test only
 */

export interface NoticeListener {
  onAdded(noticeList: NoticeDataType[]): void
  onUpdated(noticeList: NoticeDataType[]): void
  onRemoved(noticeList: NoticeDataType[]): void
  onMarkAsRead(noticeList: NoticeDataType[]): void
  onRemoveAll(noticeList: NoticeDataType[]): void
  onDismiss(): void
}

export class NotificationManager {
  #eventListenerMap = new Map<string, NoticeListener>()
  #actionNoticeDataBus: ActionNoticeDataBus = buildActionDataBus()
  #remoteNoticeList: NoticeDataType[] = []
  #LocalNoticeList: NoticeDataType[] = []
  #store: NoticeStore

  constructor() {
    this.#store = new NoticeStore()
    this.#initNoticeChangeListener()
  }

  async #initNoticeList() {
    const { data: actionNoticeData = [] } = await this.#actionNoticeDataBus.getAllNotices()
    const allActionNoticeData = this.#handleNoticeList(actionNoticeData)

    this.#store.resetByList(allActionNoticeData)
    this.#setRemoteNoticeToLocal(allActionNoticeData)
  }

  async #setRemoteNoticeToLocal(allActionNoticeData: NoticeDataType[]) {
    const LocalNoticeList = []
    const newNoticeList = []

    for (let i = 0; i < allActionNoticeData.length; i++) {
      const notice = allActionNoticeData[i]
      const cacheNoticeId = await this.#store.getNoticeById(notice.id)

      if (cacheNoticeId) {
        LocalNoticeList.push(notice)
      } else {
        newNoticeList.push(notice)
      }
    }

    this.#remoteNoticeList = [...LocalNoticeList]
    if (!newNoticeList.length) return

    this.#onAdded(newNoticeList)
  }

  #initNoticeChangeListener() {
    this.#actionNoticeDataBus.addNoticeListener({
      onAdded: (noticeList: ActionNoticeData[]) => {
        const newNoticeList = this.#handleNoticeList(noticeList)

        this.#onAdded(newNoticeList)
      },
      onUpdated: (noticeList: ActionNoticeData[]) => {
        const newNoticeList = this.#handleNoticeList(noticeList)

        this.#onUpdatedNotice(newNoticeList)
      },
      onRemoved: (noticeList: ActionNoticeData[]) => {
        this.#removeLocalNotice(noticeList)
      },
      onInitialized: () => {
        this.#initNoticeList()
      },
    })
  }

  addNoticeListener(listener: NoticeListener): string {
    const key = uuidv4()

    this.#eventListenerMap.set(key, listener)

    return key
  }

  removeNoticeListener(key: string) {
    if (this.#eventListenerMap.get(key)) {
      this.#eventListenerMap.delete(key)
    }
  }

  removeEventChangeListener(key: string): void {
    this.#eventListenerMap.delete(key)
  }

  #classifiedMessageType(noticeList: NoticeDataType[]) {
    const actionNoticeData = []
    const allNotices = [...this.#remoteNoticeList, ...this.#LocalNoticeList]

    for (let index = 0; index < allNotices.length; index++) {
      const notice = allNotices[index]
      const { id, category } = notice

      for (let idx = 0; idx < noticeList.length; idx++) {
        const element = noticeList[idx]

        const isDMSNotice = this.#remoteNoticeList.find((notice) => notice.id === element.id)

        if (id === element.id) {
          if (NoticeCategory.NOTICE === category && isDMSNotice) {
            actionNoticeData.push(id)
          }

          if (!isDMSNotice) {
            this.#LocalNoticeList = this.#LocalNoticeList.filter((localNotice) => localNotice.id !== id)
          }
        }
      }
    }

    return {
      actionNoticeData,
    }
  }

  #removeLocalNotice(noticeList: ActionNoticeData[]) {
    for (let idx = 0; idx < noticeList.length; idx++) {
      this.#remoteNoticeList = this.#remoteNoticeList.filter((notice) => notice.id !== noticeList[idx].id)
    }
  }

  #onAdded(noticeList: NoticeDataType[]) {
    for (let idx = 0; idx < noticeList.length; idx++) {
      const newNotice = noticeList[idx]
      const hasDMSNotice = !!this.#remoteNoticeList.find((notice) => notice.id === newNotice.id)

      if (!hasDMSNotice) {
        this.#remoteNoticeList.push(newNotice)
      }
    }
    this.#dispatchOnAdded(noticeList)
  }

  #dispatchOnAdded(noticeList: NoticeDataType[]) {
    for (const listener of this.#eventListenerMap.values()) {
      listener.onAdded(noticeList)
    }
  }

  #dispatchOnUpdated(noticeList: NoticeDataType[]) {
    for (const listener of this.#eventListenerMap.values()) {
      listener.onUpdated(noticeList)
    }
  }

  #dispatchOnRemoved(noticeList: NoticeDataType[]) {
    for (const listener of this.#eventListenerMap.values()) {
      listener.onRemoved(noticeList)
    }
  }

  #dispatchOnMarkAsRead(noticeList: NoticeDataType[]) {
    for (const listener of this.#eventListenerMap.values()) {
      listener.onMarkAsRead(noticeList)
    }
  }

  #dispatchOnHideNotice() {
    for (const listener of this.#eventListenerMap.values()) {
      listener.onDismiss()
    }
  }

  #dispatchOnRemoveAll(noticeList: NoticeDataType[]) {
    for (const listener of this.#eventListenerMap.values()) {
      listener.onRemoveAll(noticeList)
    }
  }

  #handleNoticeList(noticeList: NoticeType[]): NoticeDataType[] {
    const newNoticeList = noticeList.map((notice) => ({
      ...notice,
      isRead: false,
    }))

    return newNoticeList
  }

  async #updatedNotice(noticeList: NoticeDataType[], isRemotes = false) {
    for (let idx = 0; idx < noticeList.length; idx++) {
      const newNotice = noticeList[idx]
      const { id, category } = newNotice
      const isLocalNoticeList = this.#LocalNoticeList.find((notice) => notice.id === id)

      this.#store.removeCacheNoticeByKey(id)

      if (category === NoticeCategory.NOTICE && isRemotes) {
        await this.#actionNoticeDataBus.updateNoticeById(id, newNotice)
        this.#updateRemoteNotice(newNotice)
        continue
      }

      if (isLocalNoticeList) {
        this.#updateLocalNotice(newNotice)
      } else {
        this.#updateRemoteNotice(newNotice)
      }
    }
  }

  #updateLocalNotice(newNotice: NoticeDataType) {
    const index = this.#LocalNoticeList.findIndex((notice) => notice.id === newNotice.id)

    if (index >= 0) {
      this.#LocalNoticeList[index] = newNotice
    } else {
      this.#LocalNoticeList.push(newNotice)
    }
  }

  #updateRemoteNotice(newNotice: NoticeDataType) {
    const index = this.#remoteNoticeList.findIndex((notice) => notice.id === newNotice.id)

    if (index >= 0) {
      this.#remoteNoticeList[index] = newNotice
    } else {
      this.#remoteNoticeList.push(newNotice)
    }
  }

  async #onUpdatedNotice(noticeList: NoticeDataType[], isRemotes = false) {
    this.#updatedNotice(noticeList, isRemotes)
    this.#dispatchOnUpdated(noticeList)
  }

  async #onRemoveNotice(noticeList: NoticeDataType[], isRemotes = false) {
    const { actionNoticeData } = this.#classifiedMessageType(noticeList)

    if (actionNoticeData.length && isRemotes) {
      try {
        const result = await this.#actionNoticeDataBus.removeNoticesByIds(actionNoticeData)

        if (result.status === NoticeOperationResult.SUCCESS) {
          this.#removeLocalNotice(noticeList as ActionNoticeData[])
        }
      } catch (error) {
        console.log(error)
      }
    }

    this.#dispatchOnRemoved(noticeList)
  }

  addSystemInfo(sysNotice: SysNotice) {
    const notice: ActionNotice = {
      // 这里是系统通知，会存入本地信息池，不会进入mqtt通道，会有一些特殊通知的标题文案属于特定微应用，传入特定的微应用id，默认应该是系统通知
      appId: sysNotice.appId ?? SystemAppId.SYSTEM,
      category: NoticeCategory.NOTICE,
      id: uuidv4(),
      timestamp: Date.now(),
      isRead: false,
      msg: sysNotice.msg,
      type: sysNotice.type ?? NoticeActionType.INFO,
      action: sysNotice.action ?? Action.NAV_TO,
      actionBtnLabel: sysNotice.actionBtnLabel,
      isEmphasized: sysNotice.isEmphasized ?? false,
      extraQuery: sysNotice.extraQuery,
    }

    this.#addLocalNotice([notice])

    return notice
  }

  updateTransmissionNotice(sysNotice: SysTransmissionData): TransmissionNotice {
    const existIndex = this.#LocalNoticeList.findIndex(({ id }) => id == sysNotice.id)
    const localTransmissionNotice = this.#LocalNoticeList[existIndex]

    const notice: TransmissionNotice = {
      appId: SystemAppId.TRANSMISSION_CENTER,
      category: NoticeCategory.TRANSMISSION,
      timestamp: Date.now(),
      isRead: this.#getTransmissionNoticeReadState(sysNotice, localTransmissionNotice),
      isEmphasized: true,
      ...sysNotice,
    }

    if (!localTransmissionNotice) {
      this.#addLocalNotice([notice])

      return notice
    }

    if (notice.isRead) {
      this.#LocalNoticeList.splice(existIndex, 1, notice)
      this.#onUpdatedNotice([notice])
    } else {
      this.#LocalNoticeList.splice(existIndex, 1)
      this.#addLocalNotice([notice])
    }

    return notice
  }

  #getTransmissionNoticeReadState(sysNotice: SysTransmissionData, localTransmissionNotice: NoticeDataType | undefined) {
    if (!localTransmissionNotice) return false

    let localTransmissionNoticeTotalCount = 0

    if (localTransmissionNotice.category === NoticeCategory.TRANSMISSION) {
      try {
        const transmissionNotice = localTransmissionNotice as TransmissionData

        localTransmissionNoticeTotalCount =
          transmissionNotice.upload.totalCount + transmissionNotice.download.totalCount
      } catch (error) {
        console.log(error)
      }
    }

    return (
      sysNotice.upload.totalCount + sysNotice.download.totalCount <= localTransmissionNoticeTotalCount &&
      localTransmissionNotice.isRead
    )
  }

  #addLocalNotice(noticeList: NoticeDataType[]) {
    this.#LocalNoticeList.push(...noticeList)
    this.#dispatchOnAdded(noticeList)
  }

  async addNotice(noticeList: NoticeDataType[]) {
    for (let index = 0; index < noticeList.length; index++) {
      const notice = noticeList[index]

      this.#actionNoticeDataBus.appendNotice(notice as ActionNotice)
    }
  }

  removeNotice(noticeList: NoticeDataType[]) {
    this.#onRemoveNotice(noticeList, true)
  }

  updateNotice(noticeList: NoticeDataType[]) {
    this.#onUpdatedNotice(noticeList, true)
  }

  dismissFloatNotice() {
    this.#dispatchOnHideNotice()
  }

  markAsReadByNoticeList(noticeList: NoticeDataType[], isRemotes = false) {
    const newNoticeIds = []

    for (let index = 0; index < noticeList.length; index++) {
      const notice = noticeList[index]

      notice.isRead = true
      newNoticeIds.push(notice.id)
    }

    if (isRemotes) this.#store.cacheNotices(newNoticeIds)

    this.#dispatchOnMarkAsRead(noticeList)
  }

  markAllNoticeAsRead() {
    this.markAsReadByNoticeList(this.#remoteNoticeList, true)
    this.markAsReadByNoticeList(this.#LocalNoticeList)
    this.#dispatchOnRemoveAll(this.getNoticeList())
  }

  async removeAllNotice() {
    if (this.#remoteNoticeList.length) {
      try {
        const ids: string[] = this.#remoteNoticeList.map((notice) => notice.id)

        await this.#actionNoticeDataBus.removeNoticesByIds(ids)
        this.#remoteNoticeList = []
      } catch (error) {
        console.log(error)
      }
    }
    this.#LocalNoticeList = []
    this.#dispatchOnRemoveAll(this.getNoticeList())
  }

  getNoticeList() {
    const noticeList = [...this.#remoteNoticeList, ...this.#LocalNoticeList]

    return orderBy(noticeList, 'timestamp', 'desc')
  }

  getUnreadEmphasizedNotices() {
    return this.getNoticeList().filter(({ isRead, isEmphasized }) => !isRead && isEmphasized) || []
  }

  hasUnreadNotices(): boolean {
    return !!this.getNoticeList().find(({ isRead }) => !isRead)
  }

  /**
   * For test only. **DO NOT** use in production version.
   *
   * @deprecated
   * @internal
   */
  removeAllNoticeForce() {
    this.#LocalNoticeList = []
    this.#remoteNoticeList = []
  }
}
const INSTANCE = new NotificationManager()

export { INSTANCE }

const noticeIsShowed = (switchIdentifier: string): boolean => {
  const isPrompted = window.localStorage.getItem(switchIdentifier) ?? 'false'

  window.localStorage.setItem(switchIdentifier, 'true')

  return isPrompted === 'true'
}

export const generateDebounceNoticeSender = (
  sysNotice: SysNotice,
  switchIdentifier: string,
  isShowed = noticeIsShowed
) =>
  debounce(
    (sysNoticeOverride?: Partial<SysNotice>) => {
      if (isShowed(switchIdentifier)) return
      INSTANCE.addSystemInfo({ ...sysNotice, ...(sysNoticeOverride ?? {}) })
    },
    TimeConstants.DEFAULT_DEBOUNCING_IN_MS,
    { leading: true, trailing: false }
  )
